import React from "react";

import Account from "../../components/Layout/Sidebar/Account/Account";
import NavigationLink from "../../components/Layout/Sidebar/NavigationLink";
import PrivateComponent from "../../components/Private/PrivateComponent";
import OberinCalendar from "../../components/Layout/Sidebar/OberinCalendar/OberinCalendar";
import LatestPosts from "../../components/Layout/Sidebar/LatestPosts/LatestPosts";
import RecentPosts from "../../components/Layout/Sidebar/RecentPosts/RecentPosts";
import SocialMediaLinks from "../../components/Layout/Sidebar/SocialMediaLinks/SocialMediaLinks";
import { Role } from "../../utilities/roles";
import useNavigationSettings from "../../hooks/useNavigationSettings";

import "./Sidebar.scss";

interface Props {
    toggled: boolean;
    collapse: () => void;
}

const Sidebar: React.FC<Props> = React.memo(({ toggled, collapse }) => {
    const navSettings = useNavigationSettings();

    return (
        <section id="sidebar" className={toggled ? "toggled" : ""}>
            {/* Account information */}
            <Account collapse={collapse} />

            {/* Mobile: Calendar */}
            <div className="d-block d-lg-none">
                <OberinCalendar />
            </div>

            {/* Navigation */}
            <div className="w-100 px-2 py-1">
                <div className="box-shadow">
                    <div className="title-bar px-2">
                        <span>Navigation</span>
                    </div>
                    <nav className="bg-light-gray p-1 bl-xs br-xs bb-xs">
                        <ul className="sidebar-list">
                            <NavigationLink
                                collapse={collapse}
                                link="/home"
                                label="Home"
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="/download"
                                label="Play the game"
                                bold
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="/news"
                                label="News"
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="/forum"
                                label="Forums"
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="https://oberin.fandom.com/wiki/OberinWiki?utm_source=oberin_website"
                                label="Oberin Wiki"
                                direct
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="/contact"
                                label="Contacts"
                            />

                            <li className="nav-separator" aria-hidden="true" />

                            <NavigationLink
                                collapse={collapse}
                                link="/moonCycle"
                                label="Moon Cycle"
                            />
                            <NavigationLink
                                collapse={collapse}
                                link="/library"
                                label="Brigobaen Library"
                            />
                            {navSettings.forumRtqCategoryId && (
                                <NavigationLink
                                    collapse={collapse}
                                    link={`/forum/category/${navSettings.forumRtqCategoryId}`}
                                    label="RTQ News"
                                />
                            )}
                            {navSettings.forumPlayerArtCategoryId && (
                                <NavigationLink
                                    collapse={collapse}
                                    link={`/forum/category/${navSettings.forumPlayerArtCategoryId}`}
                                    label="Player Art"
                                />
                            )}
                            {navSettings.forumPlayerStoryCategoryId && (
                                <NavigationLink
                                    collapse={collapse}
                                    link={`/forum/category/${navSettings.forumPlayerStoryCategoryId}`}
                                    label="Player Stories"
                                />
                            )}

                            <li className="nav-separator" aria-hidden="true" />

                            <NavigationLink
                                collapse={collapse}
                                link="/termsOfService"
                                label="Terms of Service"
                            />
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Mobile: Last Forum Posts */}
            <div className="d-block d-lg-none">
                <div className="w-100 px-2 py-1">
                    <div className="box-shadow">
                        <div className="title-bar px-2">Last Forum Posts</div>
                        <div className="recent-post-container bg-light-gray bl-xs br-xs bb-xs">
                            <LatestPosts collapse={collapse} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Admin Menu */}
            <PrivateComponent roles={[Role.ADMIN]}>
                <div className="w-100 px-2 py-1">
                    <div className="box-shadow">
                        <div className="title-bar px-2">
                            <span>Admin Menu</span>
                        </div>
                        <nav className="bg-light-gray p-1 bl-xs br-xs bb-xs">
                            <ul className="sidebar-list">
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/dashboard"
                                    label="Dashboard"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/users"
                                    label="Users"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/roles"
                                    label="Roles"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/forums"
                                    label="Forums"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/announcements"
                                    label="Announcements"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/installationGuides"
                                    label="Installation Guides"
                                />
                                <NavigationLink
                                    collapse={collapse}
                                    link="/admin/settings"
                                    label="Settings"
                                />
                            </ul>
                        </nav>
                    </div>
                </div>
            </PrivateComponent>

            {/* Moderator menu */}
            <PrivateComponent rules={["isAllowedToModerate"]}>
                <div className="w-100 px-2 py-1">
                    <div className="box-shadow">
                        <div className="title-bar px-2">
                            <span>Moderator Menu</span>
                        </div>
                        <div className="bg-light-gray p-1 bl-xs br-xs bb-xs">
                            <ul className="sidebar-list">
                                <NavigationLink
                                    collapse={collapse}
                                    link="/moderator/moveRequests"
                                    label="Move requests"
                                />
                            </ul>
                        </div>
                    </div>
                </div>
            </PrivateComponent>

            {/* RTQ News */}
            <div className="w-100 px-2 py-1">
                <div className="box-shadow">
                    <div className="title-bar px-2">RTQ News</div>
                    <div className="recent-post-container bg-light-gray bl-xs br-xs bb-xs">
                        <RecentPosts
                            url="forumCategory/rtq"
                            collapse={collapse}
                        />
                    </div>
                </div>
            </div>

            {/* Player Stories */}
            <div className="w-100 px-2 py-1">
                <div className="box-shadow">
                    <div className="title-bar px-2">Player Stories</div>
                    <div className="recent-post-container bg-light-gray bl-xs br-xs bb-xs">
                        <RecentPosts
                            url="forumCategory/story"
                            collapse={collapse}
                        />
                    </div>
                </div>
            </div>

            {/* Player Art */}
            <div className="w-100 px-2 py-1">
                <div className="box-shadow">
                    <div className="title-bar px-2">Player Art</div>
                    <div className="recent-post-container bg-light-gray bl-xs br-xs bb-xs">
                        <RecentPosts
                            url="forumCategory/art"
                            collapse={collapse}
                        />
                    </div>
                </div>
            </div>

            {/* Mobile: Social media */}
            <div className="d-block d-lg-none">
                <div className="w-100 px-2 py-1">
                    <div className="box-shadow">
                        <div className="title-bar px-2">Social media</div>
                        <nav
                            className="bg-light-gray p-1 bl-xs br-xs bb-xs d-flex flex-column"
                            aria-label="Social media links"
                        >
                            <SocialMediaLinks />
                        </nav>
                    </div>
                </div>
            </div>
        </section>
    );
});

export default Sidebar;
